/** @format */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Typography,
    Button,
    TextField,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Fade,
} from "@mui/material";
import {
    ArrowForward,
    ArrowBack,
    CheckCircleOutline,
    Public,
    Groups,
    TipsAndUpdates,
    ConnectWithoutContact,
    FavoriteBorder,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { colors } from "../../utility/types/constant";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import axios from "axios";
import { Footer, SEO } from "../allComponents/AllComponents";

// ── Styled components ───────────────────────────────────────────────────────

const PageWrapper = styled("div")({
    minHeight: "100vh",
    background: "#f8f9fb",
});

const HeroBanner = styled("div")(({ theme }) => ({
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 60%, #1e4db7 100%)`,
    padding: "72px 24px 64px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
            "radial-gradient(ellipse at 70% 30%, rgba(30,193,149,0.15) 0%, transparent 65%)",
        pointerEvents: "none",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "52px 20px 48px",
    },
}));

const BadgeChip = styled("span")({
    display: "inline-block",
    background: "rgba(30,193,149,0.18)",
    color: colors.mainSecondary,
    border: "1px solid rgba(30,193,149,0.4)",
    borderRadius: "100px",
    padding: "4px 14px",
    fontSize: "0.72rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "16px",
});

const ProgressContainer = styled("div")({
    maxWidth: "640px",
    margin: "0 auto",
    padding: "32px 24px 0",
});

const ProgressTrack = styled("div")({
    height: "4px",
    background: "rgba(4,13,69,0.1)",
    borderRadius: "2px",
    overflow: "hidden",
    marginBottom: "8px",
});

const ProgressFill = styled("div")<{ progress: number }>(({ progress }) => ({
    height: "100%",
    width: `${progress}%`,
    background: `linear-gradient(90deg, ${colors.mainSecondary}, #143AA2)`,
    borderRadius: "2px",
    transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
}));

const FormCard = styled("div")(({ theme }) => ({
    maxWidth: "640px",
    margin: "0 auto 60px",
    padding: "0 24px",
}));

const StepCard = styled("div")(({ theme }) => ({
    background: "#ffffff",
    borderRadius: "16px",
    padding: "36px 32px",
    boxShadow: "0 2px 16px rgba(4,13,69,0.07)",
    [theme.breakpoints.down("sm")]: {
        padding: "28px 20px",
    },
}));

const SectionLabel = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: colors.mainSecondary,
    marginBottom: "6px",
});

const StepTitle = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.25rem, 3vw, 1.55rem)",
    fontWeight: 800,
    color: colors.primary,
    marginBottom: "6px",
    lineHeight: 1.3,
});

const StepSubtitle = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.92rem",
    color: "#6b7280",
    marginBottom: "28px",
    lineHeight: 1.6,
});

// Single-select card (radio-style)
const OptionCard = styled("button")<{ selected: boolean }>(({ selected }) => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "14px 18px",
    borderRadius: "10px",
    border: selected
        ? `2px solid ${colors.mainSecondary}`
        : "2px solid #e5e7eb",
    background: selected ? "rgba(30,193,149,0.07)" : "#fafafa",
    cursor: "pointer",
    marginBottom: "10px",
    transition: "all 0.18s ease",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.94rem",
    fontWeight: selected ? 600 : 400,
    color: selected ? colors.primary : "#374151",
    outline: "none",
    "&:hover": {
        border: `2px solid ${colors.mainSecondary}`,
        background: "rgba(30,193,149,0.05)",
    },
}));

// Multi-select card (checkbox-style)
const CheckCard = styled("button")<{ selected: boolean }>(({ selected }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    textAlign: "left",
    padding: "13px 16px",
    borderRadius: "10px",
    border: selected
        ? `2px solid ${colors.mainSecondary}`
        : "2px solid #e5e7eb",
    background: selected ? "rgba(30,193,149,0.07)" : "#fafafa",
    cursor: "pointer",
    marginBottom: "8px",
    transition: "all 0.18s ease",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.92rem",
    fontWeight: selected ? 600 : 400,
    color: selected ? colors.primary : "#374151",
    outline: "none",
    "&:hover": {
        border: `2px solid ${colors.mainSecondary}`,
        background: "rgba(30,193,149,0.05)",
    },
}));

const CheckIndicator = styled("span")<{ selected: boolean }>(({ selected }) => ({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    borderRadius: "5px",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #d1d5db",
    background: selected ? colors.mainSecondary : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    "& svg": {
        fontSize: "13px",
        color: "#fff",
    },
}));

const RadioIndicator = styled("span")<{ selected: boolean }>(({ selected }) => ({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    borderRadius: "50%",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #d1d5db",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    marginRight: "4px",
    "&::after": selected
        ? {
            content: '""',
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: colors.mainSecondary,
        }
        : {},
}));

const NavRow = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "32px",
    gap: "12px",
});

const PrimaryBtn = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "10px",
    padding: "12px 28px",
    textTransform: "none",
    fontSize: "0.95rem",
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
    color: "#fff",
    "&:hover": {
        background: `linear-gradient(135deg, #0a1a6b 0%, #0d2d85 100%)`,
    },
    "&:disabled": {
        opacity: 0.5,
        color: "#fff",
    },
});

const SecondaryBtn = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    borderRadius: "10px",
    padding: "12px 22px",
    textTransform: "none",
    fontSize: "0.92rem",
    color: colors.primary,
    border: `2px solid ${colors.primary}`,
    "&:hover": {
        background: "rgba(4,13,69,0.05)",
    },
});

const QuestionLabel = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
    color: colors.primary,
    marginBottom: "14px",
    marginTop: "22px",
    "&:first-of-type": {
        marginTop: 0,
    },
});

const Divider = styled("div")({
    height: "1px",
    background: "#f0f0f0",
    margin: "24px 0",
});

// ── Constants ────────────────────────────────────────────────────────────────

const COUNTRIES = [
    "United States", "Nigeria", "Ghana", "Kenya", "Ethiopia", "United Kingdom",
    "Canada", "Jamaica", "Trinidad and Tobago", "Barbados", "Guyana", "Haiti",
    "Dominican Republic", "Mexico", "Colombia", "Brazil", "Peru", "Ecuador",
    "India", "Pakistan", "Bangladesh", "Sri Lanka", "Philippines", "Indonesia",
    "Vietnam", "China", "South Korea", "Japan", "Egypt", "Morocco", "Senegal",
    "Cameroon", "Ivory Coast", "South Africa", "Uganda", "Tanzania", "Rwanda",
    "France", "Germany", "Spain", "Portugal", "Italy", "Netherlands",
    "Australia", "New Zealand", "Other",
];

const STEPS = [
    { label: "About You", icon: <Public fontSize="small" /> },
    { label: "Experience", icon: <Groups fontSize="small" /> },
    { label: "Digital", icon: <TipsAndUpdates fontSize="small" /> },
    { label: "Follow-up", icon: <ConnectWithoutContact fontSize="small" /> },
];

type FormData = {
    country: string;
    inSavingsCircle: string;
    localName: string;
    localNameOther: string;
    howManaged: string[];
    howManagedOther: string;
    biggestChallenges: string[];
    biggestChallengesOther: string;
    wouldUseDigital: string;
    importantFeatures: string[];
    trustFactors: string;
    wantsContact: string;
    name: string;
    email: string;
    contactMethod: string;
};

const INITIAL: FormData = {
    country: "",
    inSavingsCircle: "",
    localName: "",
    localNameOther: "",
    howManaged: [],
    howManagedOther: "",
    biggestChallenges: [],
    biggestChallengesOther: "",
    wouldUseDigital: "",
    importantFeatures: [],
    trustFactors: "",
    wantsContact: "",
    name: "",
    email: "",
    contactMethod: "",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function toggleArr(arr: string[], val: string): string[] {
    return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

// ── Component ────────────────────────────────────────────────────────────────

const FeedbackPage: React.FC = () => {
    const { t } = useTranslation("feedback");
    const [step, setStep] = useState(0); // 0-3 form sections, 4 = thank you
    const [form, setForm] = useState<FormData>(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const STEP_LABELS = [
        t("stepAboutYou"),
        t("stepExperience"),
        t("stepDigital"),
        t("stepFollowUp"),
    ];

    const progress = step >= 4 ? 100 : Math.round(((step + 1) / 4) * 100);

    const set = (key: keyof FormData, val: any) =>
        setForm((prev) => ({ ...prev, [key]: val }));

    const canAdvance = (): boolean => {
        if (step === 0) return !!form.country && !!form.inSavingsCircle && !!form.localName;
        if (step === 1) return form.howManaged.length > 0 && form.biggestChallenges.length > 0;
        if (step === 2) return !!form.wouldUseDigital && form.importantFeatures.length > 0;
        return true; // step 3 is optional
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API_BASE_URL}/feedback/general`, {
                country: form.country,
                inSavingsCircle: form.inSavingsCircle,
                localName: form.localName,
                localNameOther: form.localNameOther || undefined,
                howManaged: form.howManaged,
                howManagedOther: form.howManagedOther || undefined,
                biggestChallenges: form.biggestChallenges,
                biggestChallengesOther: form.biggestChallengesOther || undefined,
                wouldUseDigital: form.wouldUseDigital,
                importantFeatures: form.importantFeatures,
                trustFactors: form.trustFactors || undefined,
                wantsContact: form.wantsContact === "yes",
                name: form.name || undefined,
                email: form.email || undefined,
                contactMethod: form.contactMethod || undefined,
                locale: navigator.language?.slice(0, 5) || "en",
            });
            setStep(4);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            setError(t("errorGeneric"));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    const SingleSelect = ({
        value,
        options,
        onChange,
    }: {
        value: string;
        options: string[];
        onChange: (v: string) => void;
    }) => (
        <Box>
            {options.map((opt) => (
                <OptionCard
                    key={opt}
                    selected={value === opt}
                    onClick={() => onChange(opt)}
                    type="button"
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <RadioIndicator selected={value === opt} />
                        {opt}
                    </Box>
                </OptionCard>
            ))}
        </Box>
    );

    const MultiSelect = ({
        selected,
        options,
        onChange,
    }: {
        selected: string[];
        options: string[];
        onChange: (v: string) => void;
    }) => (
        <Box>
            {options.map((opt) => {
                const isSelected = selected.includes(opt);
                return (
                    <CheckCard
                        key={opt}
                        selected={isSelected}
                        onClick={() => onChange(opt)}
                        type="button"
                    >
                        <CheckIndicator selected={isSelected}>
                            {isSelected && (
                                <svg viewBox="0 0 12 12" width="12" height="12">
                                    <polyline
                                        points="2,6 5,9 10,3"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </CheckIndicator>
                        {opt}
                    </CheckCard>
                );
            })}
        </Box>
    );

    // ── Section renders ───────────────────────────────────────────────────────

    const renderStep0 = () => (
        <>
            <SectionLabel>{t("section1of4")}</SectionLabel>
            <StepTitle>{t("step0Title")}</StepTitle>
            <StepSubtitle>
                {t("step0Subtitle")}
            </StepSubtitle>

            <QuestionLabel>{t("qCountry")}</QuestionLabel>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel
                    sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.92rem" }}>
                    {t("countryPlaceholder")}
                </InputLabel>
                <Select
                    value={form.country}
                    label={t("countryPlaceholder")}
                    onChange={(e) => set("country", e.target.value)}
                    sx={{
                        borderRadius: "10px",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.92rem",
                    }}>
                    {COUNTRIES.map((c) => (
                        <MenuItem key={c} value={c} sx={{ fontSize: "0.92rem" }}>
                            {c}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <QuestionLabel>{t("qInSavingsCircle")}</QuestionLabel>
            <SingleSelect
                value={form.inSavingsCircle}
                options={[
                    t("inCircleYes"),
                    t("inCircleFormer"),
                    t("inCircleKnowSomeone"),
                    t("inCircleNo"),
                ]}
                onChange={(v) => set("inSavingsCircle", v)}
            />

            <QuestionLabel>{t("qLocalName")}</QuestionLabel>
            <SingleSelect
                value={form.localName}
                options={["Savings circle", "Ajo", "Susu", "Esusu", "Tanda", "ROSCA", t("localNameOther")]}
                onChange={(v) => set("localName", v)}
            />
            {form.localName === t("localNameOther") && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("localNameOtherPlaceholder")}
                    value={form.localNameOther}
                    onChange={(e) => set("localNameOther", e.target.value)}
                    sx={{
                        mt: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        fontFamily: "'Inter', sans-serif",
                    }}
                />
            )}
        </>
    );

    const renderStep1 = () => (
        <>
            <SectionLabel>{t("section2of4")}</SectionLabel>
            <StepTitle>{t("step1Title")}</StepTitle>
            <StepSubtitle>
                {t("step1Subtitle")}
            </StepSubtitle>

            <QuestionLabel>{t("qHowManaged")}</QuestionLabel>
            <MultiSelect
                selected={form.howManaged}
                options={[
                    t("managedInPerson"),
                    t("managedWhatsApp"),
                    t("managedSpreadsheet"),
                    t("managedNotebook"),
                    t("managedBankTransfer"),
                    t("managedOther"),
                ]}
                onChange={(v) => set("howManaged", toggleArr(form.howManaged, v))}
            />
            {form.howManaged.includes(t("managedOther")) && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("managedOtherPlaceholder")}
                    value={form.howManagedOther}
                    onChange={(e) => set("howManagedOther", e.target.value)}
                    sx={{
                        mt: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                />
            )}

            <Divider />

            <QuestionLabel>{t("qChallenges")}</QuestionLabel>
            <MultiSelect
                selected={form.biggestChallenges}
                options={[
                    t("challengeTracking"),
                    t("challengeLatePayments"),
                    t("challengeRecords"),
                    t("challengeWhosNext"),
                    t("challengeTrusting"),
                    t("challengeDisputes"),
                    t("challengeOrganizing"),
                    t("challengeOther"),
                ]}
                onChange={(v) =>
                    set("biggestChallenges", toggleArr(form.biggestChallenges, v))
                }
            />
            {form.biggestChallenges.includes(t("challengeOther")) && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("challengeOtherPlaceholder")}
                    value={form.biggestChallengesOther}
                    onChange={(e) => set("biggestChallengesOther", e.target.value)}
                    sx={{
                        mt: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                />
            )}
        </>
    );

    const renderStep2 = () => (
        <>
            <SectionLabel>{t("section3of4")}</SectionLabel>
            <StepTitle>{t("step2Title")}</StepTitle>
            <StepSubtitle>
                {t("step2Subtitle")}
            </StepSubtitle>

            <QuestionLabel>{t("qWouldUseDigital")}</QuestionLabel>
            <SingleSelect
                value={form.wouldUseDigital}
                options={[t("digitalYes"), t("digitalMaybe"), t("digitalNo")]}
                onChange={(v) => set("wouldUseDigital", v)}
            />

            <Divider />

            <QuestionLabel>{t("qImportantFeatures")}</QuestionLabel>
            <MultiSelect
                selected={form.importantFeatures}
                options={[
                    t("featureTracking"),
                    t("featureReminders"),
                    t("featureTransparentPayout"),
                    t("featureSecureHandling"),
                    t("featureRealTime"),
                    t("featureHistory"),
                    t("featureAnalytics"),
                    t("featureInvite"),
                ]}
                onChange={(v) =>
                    set("importantFeatures", toggleArr(form.importantFeatures, v))
                }
            />

            <Divider />

            <QuestionLabel>
                {t("qTrustFactors")}
            </QuestionLabel>
            <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={t("trustFactorsPlaceholder")}
                value={form.trustFactors}
                onChange={(e) => set("trustFactors", e.target.value)}
                sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.92rem",
                }}
            />
        </>
    );

    const renderStep3 = () => (
        <>
            <SectionLabel>{t("section4of4")}</SectionLabel>
            <StepTitle>{t("step3Title")}</StepTitle>
            <StepSubtitle>
                {t("step3Subtitle")}
            </StepSubtitle>

            <QuestionLabel>{t("qWantsContact")}</QuestionLabel>
            <SingleSelect
                value={form.wantsContact}
                options={[t("contactYes"), t("contactNo")]}
                onChange={(v) => set("wantsContact", v)}
            />

            {form.wantsContact === t("contactYes") && (
                <Fade in>
                    <Box>
                        <Divider />
                        <QuestionLabel>{t("qYourName")}</QuestionLabel>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t("namePlaceholder")}
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                        />

                        <QuestionLabel>{t("qEmailAddress")}</QuestionLabel>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            placeholder={t("emailPlaceholder")}
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                        />

                        <QuestionLabel>{t("qContactMethod")}</QuestionLabel>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t("contactMethodPlaceholder")}
                            value={form.contactMethod}
                            onChange={(e) => set("contactMethod", e.target.value)}
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                        />
                    </Box>
                </Fade>
            )}
        </>
    );

    const renderThankYou = () => (
        <Box
            sx={{
                textAlign: "center",
                py: 6,
                px: 4,
                background: "#fff",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(4,13,69,0.07)",
                maxWidth: "560px",
                mx: "auto",
                mb: 6,
            }}>
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "rgba(30,193,149,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                }}>
                <CheckCircleOutline
                    sx={{ fontSize: 44, color: colors.mainSecondary }}
                />
            </Box>

            <Typography
                sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "clamp(1.4rem, 3vw, 1.75rem)",
                    fontWeight: 800,
                    color: colors.primary,
                    mb: 2,
                    lineHeight: 1.25,
                }}>
                {t("thankYouTitle")}
            </Typography>

            <Typography
                sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.97rem",
                    color: "#6b7280",
                    lineHeight: 1.8,
                    mb: 3,
                    maxWidth: "420px",
                    mx: "auto",
                }}>
                {t("thankYouBody")}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    mb: 4,
                }}>
                {[t("tagCommunity"), t("tagSecure"), t("tagTransparent")].map((tag) => (
                    <Box
                        key={tag}
                        sx={{
                            background: "rgba(4,13,69,0.05)",
                            borderRadius: "100px",
                            px: 2,
                            py: 0.6,
                            fontSize: "0.82rem",
                            fontFamily: "'Inter', sans-serif",
                            color: colors.primary,
                            fontWeight: 500,
                        }}>
                        {tag}
                    </Box>
                ))}
            </Box>

            <PrimaryBtn href="/" sx={{ display: "inline-flex", gap: 1 }}>
                {t("backToHome")} <ArrowForward fontSize="small" />
            </PrimaryBtn>
        </Box>
    );

    const steps = [renderStep0, renderStep1, renderStep2, renderStep3];

    // ── Main render ───────────────────────────────────────────────────────────

    return (
        <>
            <SEO
                title={t("seoTitle")}
                description={t("seoDescription")}
            />
            <PageWrapper>
                {/* Hero */}
                <HeroBanner>
                    <BadgeChip>{t("badge")}</BadgeChip>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                            fontWeight: 800,
                            color: "#fff",
                            mb: 1.5,
                            letterSpacing: "-0.02em",
                        }}>
                        {t("heroTitleLine1")}<br />{t("heroTitleLine2")}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "clamp(0.9rem, 1.8vw, 1.05rem)",
                            color: "rgba(255,255,255,0.72)",
                            maxWidth: "520px",
                            mx: "auto",
                            lineHeight: 1.7,
                        }}>
                        {t("heroSubtitle")}
                    </Typography>
                </HeroBanner>

                {/* Progress bar */}
                {step < 4 && (
                    <ProgressContainer>
                        <ProgressTrack>
                            <ProgressFill progress={progress} />
                        </ProgressTrack>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: "0.78rem",
                                    color: "#9ca3af",
                                }}>
                                {t("stepOf", { current: step + 1, total: 4, label: STEP_LABELS[step] })}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    color: colors.mainSecondary,
                                }}>
                                {progress}%
                            </Typography>
                        </Box>
                    </ProgressContainer>
                )}

                {/* Form */}
                <FormCard>
                    <Box sx={{ pt: "28px" }}>
                        {step < 4 ? (
                            <StepCard>
                                <Fade in key={step}>
                                    <Box>
                                        {steps[step]()}

                                        {error && (
                                            <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
                                                {error}
                                            </Alert>
                                        )}

                                        <NavRow>
                                            {step > 0 ? (
                                                <SecondaryBtn
                                                    startIcon={<ArrowBack />}
                                                    onClick={() => {
                                                        setStep((s) => s - 1);
                                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                                    }}>
                                                    {t("back")}
                                                </SecondaryBtn>
                                            ) : (
                                                <span />
                                            )}

                                            {step < 3 ? (
                                                <PrimaryBtn
                                                    endIcon={<ArrowForward />}
                                                    disabled={!canAdvance()}
                                                    onClick={() => {
                                                        setStep((s) => s + 1);
                                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                                    }}>
                                                    {t("continue")}
                                                </PrimaryBtn>
                                            ) : (
                                                <PrimaryBtn
                                                    endIcon={
                                                        submitting ? (
                                                            <CircularProgress size={16} color="inherit" />
                                                        ) : (
                                                            <FavoriteBorder />
                                                        )
                                                    }
                                                    disabled={submitting}
                                                    onClick={handleSubmit}>
                                                    {submitting ? t("submitting") : t("submitFeedback")}
                                                </PrimaryBtn>
                                            )}
                                        </NavRow>
                                    </Box>
                                </Fade>
                            </StepCard>
                        ) : (
                            <Fade in>
                                <Box>{renderThankYou()}</Box>
                            </Fade>
                        )}
                    </Box>
                </FormCard>

                <Footer />
            </PageWrapper>
        </>
    );
};

export default FeedbackPage;
